import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";

const GROUPS = [
  { title: "Hero", fields: [
    { key: "hero_availability", label: "Availability badge (use {month} for the current month)" },
    { key: "hero_h1_line1", label: "Headline — line 1" },
    { key: "hero_h1_line2", label: "Headline — line 2 (gold)" },
    { key: "hero_bio", label: "Bio paragraph", area: true },
  ]},
  { title: "Voice FX Lab", fields: [
    { key: "fxlab_kicker", label: "Kicker" }, { key: "fxlab_title", label: "Title" }, { key: "fxlab_sub", label: "Subtitle", area: true },
  ]},
  { title: "Services", fields: [
    { key: "services_kicker", label: "Kicker" }, { key: "services_title", label: "Title" }, { key: "services_sub", label: "Subtitle", area: true },
  ]},
  { title: "Featured Project", fields: [
    { key: "featured_kicker", label: "Kicker" }, { key: "featured_title", label: "Title" }, { key: "featured_sub", label: "Subtitle", area: true },
  ]},
  { title: "Portfolio", fields: [
    { key: "portfolio_kicker", label: "Kicker" }, { key: "portfolio_title", label: "Title" }, { key: "portfolio_sub", label: "Subtitle", area: true },
  ]},
  { title: "Mastering (Before / After)", fields: [
    { key: "mastering_kicker", label: "Kicker" }, { key: "mastering_title", label: "Title" }, { key: "mastering_sub", label: "Subtitle", area: true },
  ]},
  { title: "Contact", fields: [
    { key: "contact_kicker", label: "Kicker" }, { key: "contact_title", label: "Title" }, { key: "contact_sub", label: "Subtitle", area: true },
  ]},
];

export const SiteTextManager = () => {
  const [c, setC] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/site-content").then((r) => setC(r.data)).catch(() => {}); }, []);
  const set = (k) => (e) => setC({ ...c, [k]: e.target.value });

  const socialsOn = String(c.show_socials ?? "true") !== "false";
  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/site-content", c); toast.success("Site text saved — refresh the homepage to see it"); }
    catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 sticky top-0 bg-[#0B0C10] py-2 z-10">
        <p className="text-sm text-[#A1A1AA]">Edit the headings and copy shown across your homepage.</p>
        <Button onClick={save} disabled={saving} data-testid="sitetext-save" className="bg-[#C8A97E] text-[#0B0C10] hover:bg-[#B3956D] font-semibold rounded-sm">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <><Save size={15} className="mr-1.5" /> Save All</>}
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {GROUPS.map((g) => (
          <div key={g.title} className="rounded-lg border border-white/10 bg-[#12141A] p-5">
            <h3 className="font-head text-[#C8A97E] mb-4">{g.title}</h3>
            <div className="space-y-3">
              {g.fields.map((f) => (
                <div key={f.key}>
                  <Label className="text-xs text-[#A1A1AA]">{f.label}</Label>
                  {f.area
                    ? <Textarea value={c[f.key] || ""} onChange={set(f.key)} rows={3} data-testid={`txt-${f.key}`} className="bg-[#0B0C10] border-white/10 mt-1" />
                    : <Input value={c[f.key] || ""} onChange={set(f.key)} data-testid={`txt-${f.key}`} className="bg-[#0B0C10] border-white/10 mt-1" />}
                </div>
              ))}
              {g.title === "Hero" && (
                <div className="flex items-center justify-between pt-1">
                  <Label className="text-xs text-[#A1A1AA]">Show social links</Label>
                  <button type="button" onClick={() => setC({ ...c, show_socials: socialsOn ? "false" : "true" })} data-testid="txt-show-socials"
                    className={`relative h-6 w-11 rounded-full transition-colors ${socialsOn ? "bg-[#C8A97E]" : "bg-white/15"}`}>
                    <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${socialsOn ? "left-[22px]" : "left-0.5"}`} />
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
